import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TableColumnsType } from 'antd';
import { Row, Col, Switch } from 'antd';
import { useNumericFormat } from 'react-number-format';
import {
  AlertBadge,
  Text,
  SectionBox,
  Table,
  SvgIcon,
  Loader,
} from '@/components';
import { CountrySelect } from '@/components/CountrySelect';
import { formatUSD, getFormattedDateWithTime } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  complianceCustomersActions,
  complianceListsActions,
} from '@/redux/actions';
import {
  CustomerListTableItem,
  ListsCountriesCodes,
  ListsCountries,
} from '@/api';
import {
  complianceCustomersSelectors,
  complianceListsSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import { COMPLIANCE_CUSTOMERS_LIST_PAGE_SIZE } from '@/config/complianceCustomersList';
import { ThisIsDemoData } from '../ThisIsDemoData';
import { usersDemoData } from './Users.constants';
import cx from 'classnames';

import styles from './Users.module.sass';

const ComplianceDashboardUsers = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { data: customerData, loading } = useAppSelector(
    complianceCustomersSelectors.getComplianceCustomersSlice,
  );
  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);
  const { data: complianceListsData, loading: isCountriesLoading } =
    useAppSelector(complianceListsSelectors.getComplianceListsSlice);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const data = useMemo(() => {
    return isShowDemoData ? usersDemoData : customerData;
  }, [isShowDemoData, customerData]);

  const fetchList = (page: number = 1) => {
    dispatch(
      complianceCustomersActions.listCustomers({
        limit: COMPLIANCE_CUSTOMERS_LIST_PAGE_SIZE,
        offset: (page - 1) * COMPLIANCE_CUSTOMERS_LIST_PAGE_SIZE,
      }),
    );
  };

  useEffect(() => {
    if (!isShowDemoData) {
      fetchList();
    }
  }, [isShowDemoData]);

  useEffect(() => {
    dispatch(complianceListsActions.fetchLists());
  }, [dispatch]);

  const numberFormat = useNumericFormat({
    thousandSeparator: ' ',
    decimalScale: 1,
  });

  const handleCountryChange = useCallback(
    (record: CustomerListTableItem, value: string | undefined) => {
      if (isShowDemoData) return;
      const country = value ? (value as ListsCountriesCodes) : null;
      dispatch(
        complianceCustomersActions.updateCustomer({
          id: String(record.key),
          country,
        }),
      );
    },
    [dispatch, isShowDemoData],
  );

  const handleSuspendedChange = useCallback(
    (record: CustomerListTableItem, value: boolean) => {
      if (isShowDemoData) return;
      dispatch(
        complianceCustomersActions.updateCustomer({
          id: String(record.key),
          isSuspended: value,
        }),
      );
    },
    [dispatch, isShowDemoData],
  );

  const expandedRowRender = useCallback(
    (record: CustomerListTableItem) => {
      const country = record.country;
      const isSuspended = record.isSuspended ?? false;
      const lastActivityDisplay = record.lastActivity
        ? getFormattedDateWithTime(record.lastActivity)
        : 'N/A';

      return (
        <div className={styles.expandedContent}>
          <Row
            gutter={24}
            justify="space-between"
            align="middle"
            className={styles.detailsRow}
          >
            <Col>
              <div className={cx(styles.formField, styles.formFieldCenter)}>
                <div className={styles.formLabel}>
                  {t('translation.country')}:
                </div>
                <div className={styles.countrySelectWrap}>
                  {isCountriesLoading && (
                    <div className={styles.countrySelectLoader}>
                      <Loader size="small" />
                    </div>
                  )}
                  <CountrySelect
                    value={country}
                    countries={
                      complianceListsData?.countries ?? ({} as ListsCountries)
                    }
                    disabled={isCountriesLoading || isShowDemoData || loading}
                    className={styles.countrySelect}
                    onChange={(value) =>
                      handleCountryChange(record, value as ListsCountriesCodes)
                    }
                    allowClear
                  />
                </div>
              </div>
            </Col>
            <Col>
              <div className={cx(styles.formField, styles.formFieldCenter)}>
                <label className={styles.suspendedLabel}>
                  <Switch
                    checked={isSuspended}
                    disabled={isShowDemoData || loading}
                    onChange={(checked) =>
                      handleSuspendedChange(record, checked)
                    }
                  />
                  <span>{t('translation.suspended')}</span>
                </label>
              </div>
            </Col>

            <Col className={styles.lastActivityCol}>
              <div className={styles.lastActivityRow}>
                <span className={styles.formLabel}>
                  {t('translation.last_activity')}:
                </span>
                <span className={styles.formValue}>{lastActivityDisplay}</span>
              </div>
            </Col>
          </Row>
        </div>
      );
    },
    [
      t,
      loading,
      isShowDemoData,
      isCountriesLoading,
      complianceListsData?.countries,
      handleCountryChange,
      handleSuspendedChange,
    ],
  );

  const columns: TableColumnsType<CustomerListTableItem> = useMemo(
    () => [
      {
        title: t('translation.user'),
        dataIndex: 'user',
        width: '16%',
        ellipsis: true,
        render: (value) => (
          <Text weight="medium" wrap="ellipsis">
            {value}
          </Text>
        ),
      },
      {
        title: t('translation.risk'),
        dataIndex: 'risk',
        width: '14%',
        align: 'center',
        sorter: {
          compare: (a, b) =>
            a.risk && b.risk ? a.risk.length - b.risk.length : 0,
        },
        render: (value) => (!!value ? <AlertBadge type={value} /> : ''),
      },
      {
        title: t('translation.total_transfers'),
        dataIndex: 'totalTransfers',
        width: '16%',
        sorter: {
          compare: (a, b) => a.totalTransfers - b.totalTransfers,
        },
        render: (value) => numberFormat.format?.(value.toString()),
      },
      {
        title: t('translation.total_funds'),
        dataIndex: 'totalFunds',
        width: '16%',
        sorter: {
          compare: (a, b) => a.totalFunds - b.totalFunds,
        },
        render: (value) => formatUSD(value),
      },
      {
        title: t('translation.total_alerts'),
        dataIndex: 'totalAlerts',
        width: '16%',
        sorter: {
          compare: (a, b) => a.totalAlerts - b.totalAlerts,
        },
        render: (value) => numberFormat.format?.(value.toString()),
      },
      {
        title: t('translation.new_alerts'),
        dataIndex: 'newAlerts',
        width: '16%',
        sorter: {
          compare: (a, b) =>
            a.newAlerts && b.newAlerts ? a.newAlerts - b.newAlerts : 0,
        },
        render: (value) => numberFormat.format?.(value.toString()),
      },
    ],
    [t, numberFormat],
  );

  return (
    <>
      {isShowDemoData && <ThisIsDemoData />}
      <SectionBox size="large">
        {{
          title: t('translation.users'),
          body: (
            <div className={styles.tableWrap}>
              {loading && (
                <div className={styles.blockLoader}>
                  <Loader size="medium" />
                </div>
              )}
              <Table
                columns={columns}
                dataSource={data?.entries || []}
                expandedRowRender={expandedRowRender}
                expandRowByClick={false}
                expandedRowKeys={expandedRowKeys}
                expandedRowClassName={() => styles.expandedRow}
                rowClassName={(record) => {
                  const isExpanded = expandedRowKeys.includes(record.key);
                  return isExpanded ? styles.expandedTableRow : '';
                }}
                onExpand={(expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([...expandedRowKeys, record.key]);
                  } else {
                    setExpandedRowKeys(
                      expandedRowKeys.filter((key) => key !== record.key),
                    );
                  }
                }}
                expandIcon={({ expanded, onExpand, record }) => (
                  <button
                    type="button"
                    onClick={(e) => onExpand(record, e)}
                    className={styles.expandButton}
                  >
                    <SvgIcon
                      name="arrow-right-short"
                      className={cx(styles.expandIcon, {
                        [styles.expanded]: expanded,
                      })}
                    />
                  </button>
                )}
                paginationConfig={{
                  position: ['bottomLeft'],
                  defaultCurrent: 1,
                  pageSize: COMPLIANCE_CUSTOMERS_LIST_PAGE_SIZE,
                  hideOnSinglePage: true,
                  showSizeChanger: false,
                  total: data?.total || 0,
                  onChange: (page) => fetchList(page),
                }}
              />
            </div>
          ),
        }}
      </SectionBox>
    </>
  );
};

export default ComplianceDashboardUsers;
